import {MimeType} from "@reva/shared/mime-type";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";

/**
 * Clientseitiger Timeout je GetCapabilities-Anfrage. Hängt ein externer Dienst hinter dem GeoServer
 * (z.B. basemap.de beim BKG), antwortet der GeoServer sonst minutenlang nicht (#19906).
 */
export const WMTS_CAPABILITIES_TIMEOUT_MS = 20_000;

/**
 * API interface for a GeoServer WMTS GetCapabilities request (GET {@link RevaApi.PATH_GS_WMTS}).
 *
 * The response is the raw WMTS capabilities XML document, returned as a string. It is fetched as a
 * binary blob (the endpoint serves XML, not JSON) and decoded to text. The request is aborted after
 * {@link WMTS_CAPABILITIES_TIMEOUT_MS} and then fails with `general.error.timeout`.
 */
export class WmtsCapabilitiesApiInterface extends SimpleApiInterface<string> {
    /**
     * @param layer the requested GeoServer layer
     * @param matrixset the tile matrix set of the layer
     * @param timeoutMs timeout of the request in milliseconds
     */
    constructor(
        private readonly layer: string,
        private readonly matrixset: string,
        private readonly timeoutMs: number = WMTS_CAPABILITIES_TIMEOUT_MS,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        // e.g. …/gs/wmts?layer=reva%3Awohnlagen_mikro_decorated&tilematrixset=WebMercatorQuad&service=wmts&request=GetCapabilities
        return api.buildRevaApiUrl([RevaApi.PATH_GS_WMTS], {
            layer: this.layer,
            tilematrixset: this.matrixset,
            service: "wmts",
            request: "GetCapabilities",
        });
    }

    protected async executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<string> {
        const capabilities = await executor.fetchGETBinary(
            url,
            credentials,
            MimeType.ANY,
            AbortSignal.timeout(this.timeoutMs),
        );
        return capabilities.text();
    }
}
